import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SealCheck, CheckCircle, XCircle, ArrowClockwise, ClipboardText } from "@phosphor-icons/react";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return "—"; }
};

const TABS = [
    { key: "pending_review", label: "Awaiting review" },
    { key: "approved",       label: "Approved" },
    { key: "rejected",       label: "Rejected" },
    { key: "paid",           label: "Paid · KYC pending" },
];

const KYC_TONE = {
    verified: "text-emerald-700 bg-emerald-50 border-emerald-200",
    mismatch: "text-amber-700 bg-amber-50 border-amber-200",
    pending:  "text-cobalt bg-cobalt/10 border-cobalt/30",
    failed:   "text-red-700 bg-red-50 border-red-200",
};

export default function AdminVerifiedBadge() {
    const [tab, setTab] = useState("pending_review");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/verified-badge/queue", { params: { status: tab } });
            setRows(r.data.items || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load queue");
        } finally { setLoading(false); }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    const decide = async (orderId, decision) => {
        let reason = "";
        if (decision === "reject") {
            reason = window.prompt("Reason for rejection (visible to candidate):", "");
            if (reason === null) return;
        }
        try {
            await api.post(`/admin/verified-badge/${orderId}/decision`, { decision, reason });
            toast.success(decision === "approve" ? "Approved. Candidate is now verified." : "Rejected.");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Decision failed");
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="admin-verified-badge-page">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-splash mb-1">Trust &amp; safety</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-1">Verified badge queue</h1>
            <p className="text-sm text-[#525252] mb-6">
                Approve or reject candidate KYC submissions for the blue checkmark (₹299).
            </p>

            <div className="flex items-center gap-2 mb-4 flex-wrap">
                {TABS.map((t) => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                            data-testid={`vb-tab-${t.key}`}
                            className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                                tab === t.key ? "bg-ink text-white border-ink" : "border-[#E5E5E5] hover:border-ink"
                            }`}>
                        {t.label}
                    </button>
                ))}
                <button onClick={load} data-testid="vb-refresh"
                        className="text-[10px] font-black uppercase tracking-wider px-3 py-1.5 border border-[#E5E5E5] hover:border-ink inline-flex items-center gap-1 ml-auto">
                    <ArrowClockwise size={12} weight="bold" /> Reload
                </button>
            </div>

            {loading && <p className="text-sm text-[#A3A3A3]">Loading…</p>}
            {!loading && rows.length === 0 && (
                <div className="bg-white border border-[#E5E5E5] p-8 text-center">
                    <ClipboardText size={28} weight="regular" className="text-[#A3A3A3] mx-auto mb-2" />
                    <p className="text-sm text-[#525252]">Nothing here yet.</p>
                </div>
            )}
            {rows.length > 0 && (
                <div className="space-y-3">
                    {rows.map((r, i) => (
                        <div key={r.id} data-testid={`vb-row-${i}`}
                             className="bg-white border border-[#E5E5E5] p-4 sm:p-5">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                                <div className="flex-1 min-w-0">
                                    <p className="font-heading text-lg font-black text-ink truncate" title={r.candidate_name}>
                                        {r.candidate_name || "—"}
                                    </p>
                                    <p className="text-xs text-[#525252] break-words">
                                        <span className="break-all">{r.candidate_email}</span>
                                        {r.candidate_phone ? <> · {r.candidate_phone}</> : null}
                                    </p>
                                    <p className="mt-2 text-[10px] font-mono text-[#525252] whitespace-normal">
                                        Order: {r.id.slice(0, 8)} · Paid ₹{r.amount_inr} on {fmtDate(r.paid_at)}
                                    </p>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {Object.entries(r.kyc_summary || {}).map(([doc, k]) => (
                                            <div key={doc}
                                                 className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 border ${KYC_TONE[k.status] || "text-[#525252] border-[#E5E5E5]"}`}>
                                                {doc}: {k.status || "—"}{k.id_number_last4 ? ` · ****${k.id_number_last4}` : ""}
                                            </div>
                                        ))}
                                        {!Object.keys(r.kyc_summary || {}).length && (
                                            <span className="text-[10px] text-[#A3A3A3]">No KYC uploaded</span>
                                        )}
                                    </div>
                                    {r.decision_reason && (
                                        <p className="text-xs text-[#525252] mt-2 italic">
                                            &ldquo;{r.decision_reason}&rdquo; &mdash; {r.decided_by_email}
                                        </p>
                                    )}
                                </div>
                                {tab === "pending_review" || tab === "paid" ? (
                                    <div className="flex sm:flex-col gap-2 w-full sm:w-auto sm:shrink-0">
                                        <button onClick={() => decide(r.id, "approve")}
                                                data-testid={`vb-approve-${i}`}
                                                className="inline-flex items-center justify-center gap-1 flex-1 sm:flex-none px-3 py-2 bg-emerald-600 text-white text-xs font-black uppercase tracking-wider hover:bg-emerald-700">
                                            <CheckCircle size={14} weight="fill" /> Approve
                                        </button>
                                        <button onClick={() => decide(r.id, "reject")}
                                                data-testid={`vb-reject-${i}`}
                                                className="inline-flex items-center justify-center gap-1 flex-1 sm:flex-none px-3 py-2 border border-red-300 text-red-700 text-xs font-black uppercase tracking-wider hover:bg-red-50">
                                            <XCircle size={14} weight="fill" /> Reject
                                        </button>
                                    </div>
                                ) : (
                                    <SealCheck size={28} weight="fill"
                                               className={`self-end sm:self-start ${tab === "approved" ? "text-emerald-500" : "text-[#E5E5E5]"}`} />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
